import { useMemo } from 'react';
import {
  i18nInit,
  useLanguageControlOverrides,
  useLoadLocale
} from '@elfsight/lib-i18n';
import { LOCAL_WIDGET_ID, WidgetTokenProvider } from '@elfsight/lib-utils';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import { useFontsLoader } from '@elfsight/widget-ui';
import { StorageProvider } from '@elfsight/widget-utils';
import { I18nProvider } from '@lingui/react';
import { useSettings } from '@wu/widget-core';
import CATALOG from '../i18n/languages/widget/catalog.json';
import { API_BAR_SERVICE_NAME, meta } from './constants';
import { isInlineLayout } from './utils';
import { CountdownTimerStateProvider } from './widget/countdown-timer-state-provider';
import { API_FORM_SERVICE_NAME, FormPopupProvider } from './widget/form-popup';
import { EmbedRoot } from './widget/main';
import { Widget } from './widget/widget';

const { appName } = meta;

export function CountdownTimer() {
  const {
    theme: {
      themeSettings: { font }
    },
    layout,
    actionsDuringCountdown,
    language,
    customJS,
    editMode,
    renderThumbnailMode,
    widgetToken,
    widgetId = '1'
  } = useSettings();

  const i18n = useMemo(() => i18nInit(), []);

  useFontsLoader([font]);

  const isBar = !isInlineLayout(layout);
  const hasForm = actionsDuringCountdown.some(
    ({ type }) => type === 'open-form'
  );
  useDefineAPISchema({
    [API_BAR_SERVICE_NAME]: isBar,
    [API_FORM_SERVICE_NAME]: hasForm
  });
  useCustomJS(customJS, editMode || renderThumbnailMode);

  const messages = useLoadLocale(language?.language, 'widget', i18n);
  useLanguageControlOverrides({
    language,
    messages,
    catalog: CATALOG,
    i18n
  });

  return (
    <I18nProvider i18n={i18n}>
      <WidgetTokenProvider
        widgetToken={widgetToken}
        widgetId={widgetId ?? LOCAL_WIDGET_ID}
      >
        <StorageProvider name={appName} widgetId={widgetId}>
          <CountdownTimerStateProvider>
            <FormPopupProvider>
              <PortalNestProvider>
                <EmbedRoot>
                  <Widget />
                </EmbedRoot>
              </PortalNestProvider>
            </FormPopupProvider>
          </CountdownTimerStateProvider>
        </StorageProvider>
      </WidgetTokenProvider>
    </I18nProvider>
  );
}
